import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { X, Image as ImageIcon, Check, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ImagePicker = ({ isOpen, onClose, onSelect, currentImage }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(currentImage || '');

  useEffect(() => {
    if (isOpen) {
      loadImages();
      setSelectedImage(currentImage || '');
    }
  }, [isOpen, currentImage]);

  const loadImages = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/cms/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data);
      }
    } catch (e) {
      console.error('Failed to load images:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    onSelect(selectedImage);
    onClose();
  };

  const filteredImages = images.filter(img => 
    img.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    img.alt_text?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-surface rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col border border-border-line">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-line">
          <div className="flex items-center space-x-3">
            <ImageIcon className="text-tech-blue" size={24} />
            <h2 className="text-xl font-semibold text-text-primary">Select Image</h2>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X size={24} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border-line">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={18} />
            <input
              type="text"
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-border-line bg-page-bg text-text-primary"
            />
          </div>
        </div>

        {/* Image Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-48 text-text-secondary">
              Loading images...
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-text-secondary">
              <ImageIcon size={48} className="mb-2 opacity-50" />
              <p>No images found</p>
              <p className="text-sm mt-1">Upload images in the Image Library first</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredImages.map((img) => (
                <div
                  key={img.id}
                  onClick={() => setSelectedImage(img.url)}
                  className={`relative aspect-video rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                    selectedImage === img.url 
                      ? 'border-tech-blue ring-2 ring-tech-blue/30' 
                      : 'border-transparent hover:border-border-line'
                  }`}
                >
                  <img
                    src={img.url}
                    alt={img.alt_text || img.filename}
                    className="w-full h-full object-cover"
                  />
                  {selectedImage === img.url && (
                    <div className="absolute top-2 right-2 bg-tech-blue text-white rounded-full p-1">
                      <Check size={14} />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs truncate">{img.filename}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border-line bg-page-bg">
          <div className="text-sm text-text-secondary">
            {selectedImage ? 'Image selected' : 'Select an image from library'}
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSelect}
              disabled={!selectedImage}
              className="bg-tech-blue hover:bg-tech-blue/90"
            >
              Select Image
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImagePicker;
